import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Sparkles, Loader2, SearchX } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { useAIChat } from '../config/aiChatContext';
import { askAIPrompt } from '../hooks/useSearchBox';
import { searchSite, popularSearches, logResultClick } from '../utils/searchClient';
import { pageBg } from '../components/marketplace/frames';
import { SearchResultCard } from '../components/search/SearchResultCard';
import { PopularChips } from '../components/search/SearchPanel';

const SectionTabs = ({ sections, total, active, onChange, isAr, isDarkMode }) => {
  const tabs = [{ key: 'all', label_ar: 'الكل', label_en: 'All', count: total }, ...sections];
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="search-section-tabs">
      {tabs.map((t) => (
        <button key={t.key} type="button" onClick={() => onChange(t.key)} data-testid={`search-tab-${t.key}`}
          className={`shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-semibold border transition-colors duration-200 ${
            active === t.key
              ? 'bg-[#1E9BD9] border-[#1E9BD9] text-white shadow-[0_8px_24px_rgba(30,155,217,0.3)]'
              : (isDarkMode ? 'border-white/10 text-slate-300 hover:border-[#1E9BD9]/50' : 'border-slate-200 text-slate-600 hover:border-[#1E9BD9]/50 bg-white')
          }`}>
          {isAr ? t.label_ar : t.label_en}
          <span className={`text-[11px] px-1.5 rounded-full ${active === t.key ? 'bg-white/20' : (isDarkMode ? 'bg-white/5' : 'bg-slate-100')}`}>{t.count}</span>
        </button>
      ))}
    </div>
  );
};

const EmptyResults = ({ q, popular, isAr, isDarkMode, onAskAI, onQuery }) => (
  <div className={`max-w-xl mx-auto text-center rounded-3xl border p-10 animate-fadeIn ${isDarkMode ? 'border-white/10 bg-white/[0.02]' : 'border-slate-200 bg-white'}`} data-testid="search-empty">
    <SearchX className="h-12 w-12 mx-auto text-[#1E9BD9]/70" />
    <h2 className={`mt-4 text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{isAr ? `ما لقينا نتائج لـ «${q}»` : `No results for “${q}”`}</h2>
    <p className={`mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
      {isAr ? 'جرّب كلمة ثانية، أو خلّ المساعد الذكي يساعدك يلقى اللي تبيه.' : 'Try another word, or let the AI assistant find it for you.'}
    </p>
    <button type="button" onClick={onAskAI} data-testid="search-empty-ask-ai"
      className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full font-semibold text-white bg-gradient-to-r from-[#1E9BD9] to-[#38BDF8] shadow-[0_10px_30px_rgba(30,155,217,0.35)] transition-transform duration-200 hover:scale-[1.03]">
      <Sparkles className="h-4 w-4" />{isAr ? `اسأل المساعد الذكي عن «${q}»` : `Ask the AI assistant about “${q}”`}
    </button>
    {popular.length > 0 && (
      <div className="mt-8">
        <p className={`text-xs font-bold mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{isAr ? 'الأكثر بحثاً' : 'Most searched'}</p>
        <div className="flex justify-center"><PopularChips popular={popular} isAr={isAr} isDarkMode={isDarkMode} onQuery={onQuery} testPrefix="search-empty-popular" /></div>
      </div>
    )}
  </div>
);

export default function SearchPage() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { openChat } = useAIChat();
  const isAr = language === 'ar';
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') || '').trim();
  const section = params.get('section') || 'all';
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popular, setPopular] = useState([]);
  const searchId = useRef(null);
  const lastLogged = useRef('');

  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => { popularSearches(language).then(setPopular).catch(() => {}); }, [language]);

  useEffect(() => {
    if (!q) { setData(null); return undefined; }
    let alive = true;
    const isNewQuery = lastLogged.current !== q;
    lastLogged.current = q;
    setLoading(true);
    searchSite({ q, section, language, source: params.get('src') || 'results_page', log: isNewQuery })
      .then((d) => { if (isNewQuery && d.search_id) searchId.current = d.search_id; if (alive) setData(d); })
      .catch(() => { if (alive) setData({ total: 0, sections: [], groups: [] }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, section, language]);

  const submit = (e) => { e.preventDefault(); const term = input.trim(); if (term) setParams({ q: term, src: 'results_page' }); };
  const setSection = (key) => setParams({ q, ...(key !== 'all' ? { section: key } : {}) });
  const runQuery = (text) => setParams({ q: text, src: 'popular_chip' });
  const askAI = () => {
    logResultClick(searchId.current, { section: 'ai_assistant', title: 'المساعد الذكي' });
    openChat(askAIPrompt(q, isAr));
  };
  const labelOf = (key) => {
    const s = data?.sections?.find((x) => x.key === key);
    return s ? (isAr ? s.label_ar : s.label_en) : '';
  };
  const onOpen = (item, position) => () => logResultClick(searchId.current, { section: item.section, id: item.id, title: item.title_ar || item.title_en, position });

  return (
    <div className={`min-h-screen ${pageBg(isDarkMode)}`} dir={isAr ? 'rtl' : 'ltr'} data-testid="search-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-24">
        <form onSubmit={submit} className="relative" data-testid="search-page-form">
          <Search className={`absolute top-1/2 -translate-y-1/2 ${isAr ? 'right-5' : 'left-5'} h-5 w-5 text-[#1E9BD9]`} />
          <input value={input} onChange={(e) => setInput(e.target.value)} maxLength={200} autoFocus={!q}
            placeholder={isAr ? 'ابحث عن أي شيء في CloudGM…' : 'Search anything on CloudGM…'}
            className={`w-full h-14 rounded-2xl border text-base outline-none transition-[border-color,box-shadow] duration-200 ${isAr ? 'pr-14 pl-32' : 'pl-14 pr-32'} focus:border-[#1E9BD9]/60 focus:shadow-[0_0_0_4px_rgba(30,155,217,0.12)] ${
              isDarkMode ? 'bg-[#11141F] border-white/10 text-white placeholder:text-slate-500' : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400'
            }`}
            data-testid="search-page-input" />
          <button type="submit" data-testid="search-page-submit"
            className={`absolute top-1/2 -translate-y-1/2 ${isAr ? 'left-2' : 'right-2'} h-10 px-5 rounded-xl text-sm font-semibold text-white bg-[#1E9BD9] hover:bg-[#0284C7] transition-colors`}>
            {isAr ? 'بحث' : 'Search'}
          </button>
        </form>

        {q && (
          <div className="mt-8">
            <h1 className={`text-2xl sm:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`} data-testid="search-page-title">
              {isAr ? 'نتائج البحث عن ' : 'Results for '}<span className="text-[#1E9BD9]" dir="auto">«{q}»</span>
            </h1>
            {data && !loading && (
              <p className={`mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} data-testid="search-page-count">
                {isAr ? `${data.total} نتيجة` : `${data.total} results`}
                {data.fuzzy ? (isAr ? ' — ما لقينا تطابقاً كاملاً، هذي أقرب النتائج' : ' — no exact match, showing closest results') : ''}
              </p>
            )}
          </div>
        )}

        {!q && popular.length > 0 && (
          <div className="mt-10" data-testid="search-page-popular">
            <p className={`text-xs font-bold mb-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{isAr ? 'الأكثر بحثاً' : 'Most searched'}</p>
            <PopularChips popular={popular} isAr={isAr} isDarkMode={isDarkMode} onQuery={runQuery} testPrefix="search-page-popular" />
          </div>
        )}

        {loading && (
          <div className="mt-16 flex justify-center" data-testid="search-page-loading"><Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" /></div>
        )}

        {!loading && data && data.total > 0 && (
          <div className="mt-6 space-y-10">
            <SectionTabs sections={data.sections} total={data.total} active={section} onChange={setSection} isAr={isAr} isDarkMode={isDarkMode} />
            {data.groups.map((g) => (
              <section key={g.key} data-testid={`search-group-${g.key}`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-base md:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {isAr ? g.label_ar : g.label_en} <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>({g.count})</span>
                  </h2>
                  {section === 'all' && g.count > g.items.length && (
                    <button type="button" onClick={() => setSection(g.key)} data-testid={`search-group-more-${g.key}`} className="text-sm font-semibold text-[#1E9BD9] hover:underline">
                      {isAr ? 'عرض الكل' : 'View all'}
                    </button>
                  )}
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {g.items.map((item, i) => (
                    <SearchResultCard key={`${item.section}-${item.id}`} item={item} index={i} isAr={isAr} isDarkMode={isDarkMode}
                      sectionLabel={labelOf(item.section)} onOpen={onOpen(item, i)} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {!loading && data && data.total === 0 && q && (
          <div className="mt-10"><EmptyResults q={q} popular={popular} isAr={isAr} isDarkMode={isDarkMode} onAskAI={askAI} onQuery={runQuery} /></div>
        )}
      </div>
    </div>
  );
}
